import { useEffect, useRef, useState } from 'react';
import { useNavigation } from '@remix-run/react';

/**
 * A thin, non-blocking loading bar pinned to the top of the viewport.
 * Animates while a navigation is in flight and fades out when idle.
 * Unlike a full-screen overlay, it never covers content or blocks interaction.
 */
export function TopProgressBar() {
  const navigation = useNavigation();
  const isActive = navigation.state !== 'idle';

  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const clearTimers = () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];
    };

    if (isActive) {
      clearTimers();
      setVisible(true);
      // Jump in quickly, then creep toward 90% so it feels responsive but never "finishes" early.
      setProgress(10);
      timers.current.push(window.setTimeout(() => setProgress(45), 100));
      timers.current.push(window.setTimeout(() => setProgress(70), 300));
      timers.current.push(window.setTimeout(() => setProgress(90), 800));
    } else if (visible) {
      // Snap to 100%, then fade out and reset.
      clearTimers();
      setProgress(100);
      timers.current.push(
        window.setTimeout(() => {
          setVisible(false);
          setProgress(0);
        }, 250)
      );
    }

    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-0.5 pointer-events-none"
      role="progressbar"
      aria-hidden="true"
    >
      <div
        className="h-full bg-black transition-all duration-200 ease-out"
        style={{
          width: `${progress}%`,
          opacity: progress >= 100 ? 0 : 1,
        }}
      />
    </div>
  );
}
